import React, { useEffect, useMemo, useState } from 'react';
import '../fonts/breuer-headline.css';
import './thankyou.css';
import defaultBadgeImg from '../sortandtoss/img/badge.png';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchThemeData } from '../../admin/themeSlice';
import { selectAdminToken } from '../../admin/sessionSlice';
import { setBackButtonUrl } from '../uiSlice';
import { countThemeBeltItems, getDvSourceImageUrl } from '../../functions/themeAssets';

/** Total draggable items for Sort & Toss (respects per-category image vs name mode). */
function getSortTossItemCount(theme) {
  return countThemeBeltItems(theme);
}

function getAdminPointsPerCorrect(theme) {
  const raw = parseInt(theme?.points, 10);
  return Number.isFinite(raw) && raw > 0 ? raw : 1;
}

function ThankYou() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const adminToken = useSelector(selectAdminToken);
  const { data: themeData } = useSelector((state) => state.theme);

  const nav = location.state;
  const [points, setPoints] = useState(
    typeof nav?.points === 'number' ? nav.points : 0
  );
  const [time, setTime] = useState(
    nav?.time != null && nav.time !== '' ? String(nav.time) : ''
  );
  const [badgeImgFailed, setBadgeImgFailed] = useState(false);

  useEffect(() => {
    dispatch(fetchThemeData({ themeId: null }));
  }, [dispatch]);

  useEffect(() => {
    if (user?.backButtonRedirect != null) {
      dispatch(setBackButtonUrl(user.backButtonRedirect));
    }
  }, [user, dispatch]);

  useEffect(() => {
    const stored = JSON.parse(sessionStorage.getItem('userData') || '{}') || {};
    const uid =
      user?.userId ??
      user?.id ??
      stored.userId ??
      stored.userid ??
      stored.id;
    const sid = stored.sessionId ?? user?.sessionId;
    const oid = stored.organizationId ?? user?.organizationId;
    if (!uid || !sid || !oid || !adminToken) return;

    const syncReport = async () => {
      const backendBase = String(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
      if (!backendBase) return;

      try {
        const res = await fetch(`${backendBase}/fetchReport`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: uid,
            sessionId: sid,
            organizationId: oid,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!data.success || !data.report) return;
        const r = data.report;
        if (r.points !== undefined && r.points !== null) {
          setPoints(Number(r.points) || 0);
        }
        const t = r.time;
        if (t != null && String(t).trim() !== '') {
          setTime(String(t).trim());
        }
      } catch (e) {
        console.error(e);
      }
    };

    syncReport();
  }, [user, adminToken]);

  const badgeSrc = useMemo(() => {
    const themeBadge = String(themeData?.badge_image ?? '').trim();
    if (themeBadge) return getDvSourceImageUrl(themeBadge);
    return defaultBadgeImg;
  }, [themeData?.badge_image]);

  const showBadgeBlock = useMemo(() => {
    const totalItems = getSortTossItemCount(themeData);
    const perCorrect = getAdminPointsPerCorrect(themeData);
    const maxPossible = totalItems * perCorrect;
    if (maxPossible <= 0) return false;
    return points > maxPossible * 0.5;
  }, [themeData, points]);

  useEffect(() => {
    setBadgeImgFailed(false);
  }, [showBadgeBlock, badgeSrc]);

  const centerThankYouContent = !showBadgeBlock || badgeImgFailed;

  const thankYouDisplayText = useMemo(() => {
    const standard = themeData?.custom_text_thank_you_page;
    if (!showBadgeBlock) return standard;
    const badgeMsg = String(themeData?.custom_text_thank_you_page_badge_earned ?? '').trim();
    return badgeMsg || standard;
  }, [showBadgeBlock, themeData]);

  const thankYouTextColor = useMemo(() => {
    const raw =
      themeData?.landing_page_title_color ??
      themeData?.landing_page_title_colour;
    if (raw == null || raw === '') return undefined;
    const c = String(raw).trim();
    return c || undefined;
  }, [themeData]);

  const leaderboardButtonTextColor = useMemo(() => {
    const raw = themeData?.button_Textcolor ?? themeData?.button_text_color;
    if (raw == null || raw === '') return undefined;
    const c = String(raw).trim();
    return c || undefined;
  }, [themeData]);

  return (
    <div
      className={`thank-you-container${centerThankYouContent ? ' thank-you-container--centered' : ''}`}
      style={thankYouTextColor ? { color: thankYouTextColor } : undefined}
    >
      {showBadgeBlock ? (
        <>
          {thankYouDisplayText ? (
            <h1 className="thank-you-heading thank-you-heading--prewrap">{thankYouDisplayText}</h1>
          ) : null}
          {!badgeImgFailed ? (
            <img
              src={badgeSrc}
              className="thank-you-gif"
              alt="Badge earned"
              onError={() => setBadgeImgFailed(true)}
            />
          ) : null}
        </>
      ) : (
        thankYouDisplayText ? <div className="thank-you-text">{thankYouDisplayText}</div> : null
      )}

      {/* <div className="thank-you-text thank-you-score-summary">{scoreSummary}</div> */}

      <button
        type="button"
        className="thank-you-leaderboard-btn"
        style={
          leaderboardButtonTextColor
            ? { color: leaderboardButtonTextColor }
            : undefined
        }
        onClick={() => navigate('/leaderboard')}
      >
        Leaderboard
      </button>
    </div>
  );
}

export default ThankYou;
